import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { PopoverContent } from "@/components/ui/popover";

interface HoverPopoverProps {
  /** Element that opens the popover on hover/click. */
  trigger: React.ReactNode;
  children: React.ReactNode;
  /** Extra classes for the popover content card. */
  contentClassName?: string;
  align?: React.ComponentProps<typeof PopoverContent>["align"];
  side?: React.ComponentProps<typeof PopoverContent>["side"];
  sideOffset?: number;
  /** Wrapper element classes for the trigger. */
  triggerClassName?: string;
}

/**
 * Popover that opens on hover OR click. Hovering the trigger (or content)
 * opens it; moving away closes it after a short grace period. Clicking the
 * trigger "pins" it open so the content stays put for interaction, and an
 * outside click / Escape / second click dismisses it.
 */
export const HoverPopover = ({
  trigger,
  children,
  contentClassName,
  align = "center",
  side,
  sideOffset = 6,
  triggerClassName = "inline-flex items-center",
}: HoverPopoverProps) => {
  const [open, setOpen] = React.useState(false);
  const [pinned, setPinned] = React.useState(false);
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const openNow = () => {
    clearCloseTimer();
    setOpen(true);
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => {
      if (!pinned) setOpen(false);
    }, 150);
  };

  React.useEffect(() => clearCloseTimer, []);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setPinned(false);
  };

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <PopoverPrimitive.Anchor asChild>
        <span
          className={triggerClassName}
          onMouseEnter={openNow}
          onMouseLeave={scheduleClose}
          onClick={(e) => {
            e.stopPropagation();
            if (pinned) {
              setPinned(false);
              setOpen(false);
            } else {
              setPinned(true);
              openNow();
            }
          }}
        >
          {trigger}
        </span>
      </PopoverPrimitive.Anchor>
      <PopoverContent
        align={align}
        side={side}
        sideOffset={sideOffset}
        className={contentClassName}
        onMouseEnter={openNow}
        onMouseLeave={scheduleClose}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {children}
      </PopoverContent>
    </PopoverPrimitive.Root>
  );
};
